import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useDataCatalog, useBulkClassify } from '@/hooks/useDataCatalog';
import { ClassificationBadge } from '@/components/shared/ClassificationBadge';
import { SensitivityBadge } from '@/components/shared/SensitivityBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ApiRequestError } from '@/types/api';
import type { GatedResource, Classification, Sensitivity } from '@/types/gated-resource';

const CLASSIFICATION_OPTIONS: { value: Classification; label: string }[] = [
  { value: 'public', label: 'Public' },
  { value: 'internal', label: 'Internal' },
  { value: 'confidential', label: 'Confidential' },
  { value: 'restricted', label: 'Restricted' },
];

const SENSITIVITY_OPTIONS: { value: Sensitivity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function bulkClassifyErrorMessage(err: unknown): string {
  return err instanceof ApiRequestError
    ? err.message
    : 'Could not update classification. Please try again.';
}

export function DataCatalogPage() {
  const navigate = useNavigate();
  const [classification, setClassification] = useState<Classification | ''>('');
  const [sensitivity, setSensitivity] = useState<Sensitivity | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkClassification, setBulkClassification] = useState<Classification | ''>('');
  const [bulkSensitivity, setBulkSensitivity] = useState<Sensitivity | ''>('');
  const [sourceClassification, setSourceClassification] = useState<Classification | ''>('');
  const selectAllRef = useRef<HTMLInputElement>(null);

  const filters = {
    ...(classification ? { classification } : {}),
    ...(sensitivity ? { sensitivity } : {}),
  };

  const { data, isLoading } = useDataCatalog(Object.keys(filters).length > 0 ? filters : undefined);
  const bulkClassifyMutation = useBulkClassify();

  const items = useMemo(() => data?.data ?? [], [data]);

  // Drop selections for resources that are no longer on the current page
  // (filter change, pagination, or a refresh after a mutation).
  useEffect(() => {
    setSelectedIds((prev) => {
      const visibleIds = new Set(items.map((item) => item.id));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [items]);

  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const someSelected = items.some((item) => selectedIds.has(item.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  // Every resource currently shown carries its own source_connector_id/name, so
  // this is reliable to compute directly from the loaded page (no extra fetch).
  const commonSource = useMemo(() => {
    if (items.length === 0) return null;
    const [first, ...rest] = items;
    const sameSource = rest.every((item) => item.source_connector_id === first.source_connector_id);
    return sameSource ? { id: first.source_connector_id, name: first.source_connector_name } : null;
  }, [items]);

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        items.forEach((item) => next.delete(item.id));
      } else {
        items.forEach((item) => next.add(item.id));
      }
      return next;
    });
  }

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function handleApplyToSelected() {
    if ((!bulkClassification && !bulkSensitivity) || selectedIds.size === 0) return;
    try {
      const result = await bulkClassifyMutation.mutateAsync({
        resource_ids: Array.from(selectedIds),
        ...(bulkClassification ? { classification: bulkClassification } : {}),
        ...(bulkSensitivity ? { sensitivity: bulkSensitivity } : {}),
      });
      toast.success(`Updated ${result.updated} resource${result.updated === 1 ? '' : 's'}.`);
      setSelectedIds(new Set());
      setBulkClassification('');
      setBulkSensitivity('');
    } catch (err) {
      toast.error(bulkClassifyErrorMessage(err));
    }
  }

  async function handleApplyToSource(sourceConnectorId: string, sourceConnectorName: string) {
    if (!sourceClassification) return;
    try {
      const result = await bulkClassifyMutation.mutateAsync({
        source_connector_id: sourceConnectorId,
        classification: sourceClassification,
      });
      toast.success(
        `Classification updated for ${result.updated} resource${result.updated === 1 ? '' : 's'} from ${sourceConnectorName}.`
      );
      setSourceClassification('');
    } catch (err) {
      toast.error(bulkClassifyErrorMessage(err));
    }
  }

  const columns: Column<GatedResource>[] = [
    {
      key: 'select',
      header: (
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          aria-label="Select all resources on this page"
          className="h-3.5 w-3.5 rounded border-input accent-primary"
        />
      ),
      render: (item) => (
        <input
          type="checkbox"
          checked={selectedIds.has(item.id)}
          onChange={() => toggleRow(item.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${item.title}`}
          className="h-3.5 w-3.5 rounded border-input accent-primary"
        />
      ),
      className: 'w-10',
    },
    {
      key: 'title',
      header: 'Title',
      render: (item) => (
        <div>
          <p className="font-medium">{item.title}</p>
          {item.description && <p className="text-xs text-muted-foreground truncate max-w-xs">{item.description}</p>}
        </div>
      ),
    },
    {
      key: 'classification',
      header: 'Classification',
      render: (item) => <ClassificationBadge classification={item.classification} />,
    },
    {
      key: 'sensitivity',
      header: 'Sensitivity',
      render: (item) => <SensitivityBadge sensitivity={item.sensitivity} />,
    },
    {
      key: 'domain',
      header: 'Domain',
      render: (item) => <span className="text-muted-foreground">{item.domain}</span>,
    },
    {
      key: 'labels',
      header: 'Labels',
      render: (item) => (
        <div className="flex flex-wrap gap-1">
          {(item.labels ?? []).slice(0, 3).map((label) => (
            <Badge key={label} variant="outline" className="text-xs">{label}</Badge>
          ))}
          {(item.labels ?? []).length > 3 && <Badge variant="outline" className="text-xs">+{item.labels.length - 3}</Badge>}
        </div>
      ),
    },
    {
      key: 'chunks',
      header: 'Chunks',
      render: (item) => <span className="text-muted-foreground">{item.chunk_count ?? (item.chunk_ids ?? []).length}</span>,
      className: 'text-center',
    },
    {
      key: 'source',
      header: 'Source',
      render: (item) => <span className="text-xs text-muted-foreground">{item.source_connector_name}</span>,
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Connect"
        title="Data Catalog"
        subtitle="Browse and manage gated resources ingested from your vector databases."
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={classification || '__all__'} onValueChange={(v) => setClassification(v === '__all__' ? '' : v as Classification)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Classifications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Classifications</SelectItem>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
            <SelectItem value="confidential">Confidential</SelectItem>
            <SelectItem value="restricted">Restricted</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sensitivity || '__all__'} onValueChange={(v) => setSensitivity(v === '__all__' ? '' : v as Sensitivity)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Sensitivities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Sensitivities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar: appears once at least one row is selected. */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Select
            value={bulkClassification}
            onValueChange={(v) => setBulkClassification(v as Classification)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Set classification" />
            </SelectTrigger>
            <SelectContent>
              {CLASSIFICATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={bulkSensitivity}
            onValueChange={(v) => setBulkSensitivity(v as Sensitivity)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Set sensitivity" />
            </SelectTrigger>
            <SelectContent>
              {SENSITIVITY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={(!bulkClassification && !bulkSensitivity) || bulkClassifyMutation.isPending}
            onClick={handleApplyToSelected}
          >
            Apply to {selectedIds.size} selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </Button>
        </div>
      )}

      {/* Classify-by-source affordance: since the table is paginated, this covers
          every resource under the connector, not just the rows on this page. */}
      {commonSource && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-3">
          <span className="text-sm text-muted-foreground">
            All resources shown are from <span className="font-medium text-foreground">{commonSource.name}</span>.
          </span>
          <Select
            value={sourceClassification}
            onValueChange={(v) => setSourceClassification(v as Classification)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Set classification" />
            </SelectTrigger>
            <SelectContent>
              {CLASSIFICATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            disabled={!sourceClassification || bulkClassifyMutation.isPending}
            onClick={() => handleApplyToSource(commonSource.id, commonSource.name)}
          >
            Set classification for all from {commonSource.name}
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            This updates every resource under this connector, not just the {items.length} shown on this page.
          </p>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-12 w-12" />}
          title="No resources in catalog"
          description="Resources appear here after being ingested through pipelines. Set up a connector and pipeline to get started."
        />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          keyExtractor={(item) => item.id}
          onRowClick={(item) => navigate(`/data-catalog/${item.id}`)}
        />
      )}
    </div>
  );
}
